import type { Recipe } from '../types';

// Layered chicken enchilada casserole: enchiladas without the rolling. Made
// with the house Tortilla Land raw tortillas (cooked first — that is the step
// that makes or breaks it), a from-scratch red sauce the chicken poaches in,
// and black beans, corn and spinach folded into the filling.

export const chickenEnchiladaBake: Recipe = {
  slug: 'chicken-enchilada-bake',
  title: 'Chicken Enchilada Bake',
  meal: 'dinner',
  category: 'meal',
  types: ['bake', 'casserole'],
  cuisines: ['mexican'],
  traits: ['freezer-friendly', 'make-ahead', 'hidden-veg', 'high-protein'],
  yield: 'one 9x13 pan — 8 servings',
  activeTime: '50 min',
  totalTime: '1 hr 30 min',
  freezerLife: '3 months',
  difficulty: 2,

  summary:
    "Chicken enchiladas built like a lasagna: cooked tortillas, a filling of shredded chicken, black beans, corn and spinach, homemade red enchilada sauce and melted Monterey Jack, three layers deep in a 9x13. Assemble it, then either bake it tonight or wrap it and freeze it unbaked.",

  flavorNotes:
    "Saucy, cheesy and comforting, with a mild, earthy chili flavor — warm, not hot. The homemade sauce tastes deeper and less tinny than canned. Flour tortillas go soft and tender in the sauce, a bit like noodles in lasagna; that is how this dish is meant to be, not a mistake. The top should be bubbly and browned at the edges. Salsa, sour cream and cilantro at the table bring the brightness.",

  ingredients: [
    {
      id: 'tortillas',
      name: 'Tortilla Land uncooked flour tortillas',
      total: '10',
      prep: 'RAW — cooked in a dry skillet first, then cut in half',
      shop: 'Tortilla Land uncooked flour tortillas, 18 ct',
      uses: [{ step: 1, amount: '10' }],
    },
    {
      id: 'avocado-oil',
      name: 'Avocado oil',
      total: '¼ cup + 1 tbsp',
      uses: [
        { step: 2, amount: '¼ cup', note: 'for the sauce' },
        { step: 4, amount: '1 tbsp', note: 'for the onion' },
      ],
    },
    {
      id: 'ap-flour',
      name: 'All-purpose flour',
      total: '¼ cup',
      uses: [{ step: 2, amount: '¼ cup' }],
    },
    {
      id: 'chili-powder',
      name: 'Chili powder, mild',
      total: '¼ cup',
      shop: 'Great Value chili powder, 2.5 oz',
      uses: [{ step: 2, amount: '¼ cup' }],
    },
    {
      id: 'cumin',
      name: 'Ground cumin',
      total: '2 tsp',
      uses: [{ step: 2, amount: '2 tsp' }],
    },
    {
      id: 'garlic-powder',
      name: 'Garlic powder',
      total: '1 tsp',
      uses: [{ step: 2, amount: '1 tsp' }],
    },
    {
      id: 'onion-powder',
      name: 'Onion powder',
      total: '1 tsp',
      uses: [{ step: 2, amount: '1 tsp' }],
    },
    {
      id: 'oregano',
      name: 'Dried oregano',
      total: '1 tsp',
      uses: [{ step: 2, amount: '1 tsp' }],
    },
    {
      id: 'tomato-paste',
      name: 'Tomato paste',
      total: '3 tbsp',
      uses: [{ step: 2, amount: '3 tbsp' }],
    },
    {
      id: 'broth',
      name: 'Chicken broth',
      total: '4 cups',
      prep: 'Better Than Bouillon chicken base, 1 tsp per cup of hot water',
      uses: [{ step: 2, amount: '4 cups' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '1 tsp',
      uses: [{ step: 2, amount: '1 tsp' }],
    },
    {
      id: 'vinegar',
      name: 'White vinegar',
      total: '1 tsp',
      uses: [{ step: 2, amount: '1 tsp', note: 'wakes the sauce up' }],
    },
    {
      id: 'chicken',
      name: 'Boneless skinless chicken thighs',
      total: '2½ lb',
      prep: 'thighs stay juicy through baking, freezing and reheating; breasts dry out',
      shop: 'Perdue Harvestland free-range boneless skinless chicken thighs, about 2.5 lb (size off the tray minimum)',
      uses: [{ step: 3, amount: '2½ lb' }],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '1 large',
      prep: 'diced small',
      shop: 'Fresh yellow onion, each (1)',
      uses: [{ step: 4, amount: '1 large' }],
    },
    {
      id: 'spinach',
      name: 'Fresh spinach',
      total: '10 oz',
      prep: 'roughly chopped — it cooks down to almost nothing',
      uses: [{ step: 4, amount: '10 oz', note: 'wilted into the filling' }],
    },
    {
      id: 'black-beans',
      name: 'Black beans, 15 oz cans',
      total: '2 cans',
      prep: 'drained and rinsed',
      shop: 'Great Value black beans, 15 oz can (2)',
      uses: [{ step: 4, amount: '2 cans' }],
    },
    {
      id: 'corn',
      name: 'Frozen corn',
      total: '1½ cups',
      uses: [{ step: 4, amount: '1½ cups' }],
    },
    {
      id: 'green-chiles',
      name: 'Diced green chiles, 4 oz can',
      total: '1 can',
      prep: 'mild',
      uses: [{ step: 4, amount: '1 can' }],
    },
    {
      id: 'monterey-jack',
      name: 'Monterey Jack cheese',
      total: '3 cups',
      prep: 'shredded from a block — pre-shredded has anti-caking powder and melts grainy',
      shop: 'Tillamook Monterey Jack cheese block, 8 oz (2)',
      uses: [{ step: 5, amount: '3 cups', note: '1 cup per layer' }],
    },
    {
      id: 'sour-cream',
      name: 'Sour cream',
      total: '1 tub',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy: 'Cool and tangy on top of a hot square. Goes on at the table, never into the pan.',
      shop: 'Daisy sour cream, 16 oz',
      uses: [{ step: 6, amount: 'a spoonful each', note: 'at the table' }],
    },
    {
      id: 'cilantro',
      name: 'Fresh cilantro',
      total: '½ bunch',
      prep: 'chopped',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy: 'Fresh, bright finish. Skip if nobody in the house likes it.',
      uses: [{ step: 6, amount: 'a sprinkle', note: 'at the table' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Cook the tortillas first, then halve them',
      time: '12 min',
      critical: true,
      ingredientIds: ['tortillas'],
      body:
        "These tortillas come raw. Cook {10|tortillas} of them one at a time in a dry skillet over medium heat, about 45 seconds a side, until they puff and pick up light brown freckles. Stack them on a plate to cool, then cut the whole stack in half so you have straight edges to line the pan with.",
      tip:
        "Skip this and the layers bake into gummy raw dough — the single biggest way to ruin this dish. A just-cooked tortilla is also sturdier than a shelf-stable one, so the layers hold together when you cut squares.",
    },
    {
      n: 2,
      title: 'Make the red enchilada sauce',
      time: '10 min',
      critical: true,
      ingredientIds: ['avocado-oil', 'ap-flour', 'chili-powder', 'cumin', 'garlic-powder', 'onion-powder', 'oregano', 'tomato-paste', 'broth', 'salt', 'vinegar'],
      body:
        "This is the enchilada sauce. In a wide pot over medium, heat {¼ cup|avocado-oil} of avocado oil, whisk in {¼ cup|ap-flour} of flour and stir 1 minute to make a roux. Whisk in {¼ cup|chili-powder} of chili powder, {2 tsp|cumin} of cumin, {1 tsp|garlic-powder} of garlic powder, {1 tsp|onion-powder} of onion powder and {1 tsp|oregano} of oregano for 30 seconds, then {3 tbsp|tomato-paste} of tomato paste. Slowly whisk in {4 cups|broth} of broth so it stays smooth. Add {1 tsp|salt} of salt, bring to a simmer and cook 5 minutes until it coats a spoon. Stir in {1 tsp|vinegar} of vinegar.",
      tip:
        "Keep the heat at medium while the spices are in — chili powder scorches and turns bitter in seconds. Add the broth a splash at a time at first, whisking hard, and there will be no lumps.",
    },
    {
      n: 3,
      title: 'Poach the chicken in the sauce',
      time: '20 min',
      ingredientIds: ['chicken'],
      body:
        "Lay the {2½ lb|chicken} of chicken thighs into the simmering sauce to poach: cover, and keep it at a gentle bubble for 18 to 20 minutes until the chicken reaches 175°F inside. Lift the chicken onto a cutting board and shred it with two forks. Take the sauce off the heat.",
      tip:
        "Poaching the chicken in the sauce flavors the meat and the sauce at the same time, and it saves a pan.",
    },
    {
      n: 4,
      title: 'Build the filling',
      time: '8 min',
      ingredientIds: ['avocado-oil', 'onion', 'spinach', 'black-beans', 'corn', 'green-chiles'],
      body:
        "In a large skillet heat {1 tbsp|avocado-oil} of oil over medium and cook the onion about 5 minutes until soft. Add the {10 oz|spinach} of spinach a handful at a time and stir until it wilts. Turn off the heat and stir in the shredded chicken, {2 cans|black-beans} of black beans, {1½ cups|corn} of corn, {1 can|green-chiles} of green chiles and {1 cup} of the sauce.",
    },
    {
      n: 5,
      title: 'Layer it in a 9x13',
      time: '10 min',
      ingredientIds: ['monterey-jack'],
      body:
        "Spread about {½ cup} of sauce over the bottom of a 9x13 pan. Then build three layers, each one: tortilla halves to cover the pan (straight edges against the sides, overlapping a little), one third of the filling, about 1 cup of sauce, and {1 cup|monterey-jack} of cheese. The last layer ends with the remaining sauce and cheese on top. Stop here if this pan is going to the freezer.",
      tip:
        "Use a foil or disposable pan for the freezer so the good baking dish isn't stuck in there for months.",
    },
    {
      n: 6,
      title: 'Bake (or freeze)',
      time: '35 min',
      critical: true,
      ingredientIds: ['sour-cream', 'cilantro'],
      body:
        "To eat now: cover with foil and bake at 375°F for 20 minutes, then uncover and bake 15 minutes more until the sauce bubbles at the edges and the cheese is melted and spotty brown. Let it rest 10 minutes before cutting — it sets up and cuts into clean squares. To freeze: don't bake it; see the freezing notes.",
    },
  ],

  freezing:
    "Freeze it assembled but unbaked. Cool completely, wrap the pan tightly in plastic wrap and then foil, write the date and the baking directions on top, and freeze up to 3 months. Baked leftovers also freeze well: cut into squares, wrap each, bag them.",

  reheat: [
    {
      method: 'Whole pan, thawed',
      best: true,
      detail:
        "Thaw in the fridge 24 to 48 hours. Remove the plastic wrap, put the foil back on, bake at 375°F for 40 minutes, then uncover for 15 minutes until bubbling in the center. Rest 10 minutes.",
    },
    {
      method: 'Whole pan, from frozen',
      detail:
        "Remove the plastic wrap, foil back on, 375°F for about 1 hour 30 minutes, then uncovered 15 minutes. Check that the center is hot before serving.",
    },
    {
      method: 'Single square',
      detail:
        "Microwave a thawed square covered, 2 to 3 minutes; from frozen, 4 to 5 minutes at 50% power, then 1 minute at full.",
    },
  ],

  glossary: [
    {
      term: 'roux',
      plain:
        "Fat and flour cooked together for a minute before liquid goes in. It is what thickens the sauce so it clings instead of running thin and watery.",
    },
    {
      term: 'poach',
      plain:
        "Cook something gently in liquid that is barely bubbling. Gentle heat keeps the chicken tender.",
    },
    {
      term: 'enchilada sauce',
      plain:
        "A smooth red sauce of chili powder, broth and a little tomato. Homemade takes 10 minutes and tastes far better than the canned kind, which often has a metallic taste and added gums.",
    },
  ],

  inspiration: [
    { label: 'The Recipe Rebel — Chicken enchilada casserole', url: 'https://www.thereciperebel.com/chicken-enchilada-casserole/' },
    { label: 'Inquiring Chef — Chicken enchilada casserole', url: 'https://inquiringchef.com/chicken-enchilada-casserole/' },
  ],

  whereWeVeered:
    "Both source recipes allow flour or corn tortillas; we use the house Tortilla Land raw flour tortillas and added the step of cooking them first, which neither source needs because they use ready-made ones. The sauce is a roux-based red sauce with chili powder and broth rather than Recipe Rebel's crushed-tomato version — it is closer to classic enchilada sauce and freezes smoother. From Inquiring Chef we took the black beans, corn and green chiles in the filling; we added a full bag of spinach as the hidden vegetable. Chicken thighs instead of breasts because they survive freezing and reheating without drying out. Cheese is shredded from a block.",
};
